import { useState, useEffect, useRef, useMemo } from 'react';
import { format, parse } from 'date-fns';

import DatePickerProps, { DateSlotBase, SelectedValues } from './type';
import { Box } from '../Box';
import Icon from '../Icon';
import Loader from '../Loader';
import Modal from '../Modal';
import TextField from '../TextField';
import { TucarTheme } from '../theme';
import Calendar from './Calendar';
import {
  applyDefaultDate,
  compareTwoDates,
  defaultMaxDate,
  defaultMinDate,
  getCalendarContent,
  getWeekSelectedWeekLimits,
  getDefaultDateBuilder,
  isValidDate,
  isValidDateString,
  removeSpecialCharacters,
} from './utils';
import { DatePickerWrapperStyled } from './styled';

const DatePicker = ({ ...props }: DatePickerProps) => {
  const {
    anchorOrigin,
    baseElement,
    disabled,
    disableFuture,
    disablePast,
    fullWidth,
    helperText: helperProp,
    helperType,
    inputFormat,
    isWeekly,
    desktopMode,
    label,
    loading,
    maxDate: rawMaxDate,
    minDate: rawMinDate,
    onChange,
    openTo,
    style,
    shouldDisableDate,
    shouldDisableMonth,
    shouldDisableYear,
    value: rawValue,
    views,
  } = props;

  const value = applyDefaultDate(rawValue, null, inputFormat);
  const maxDateProp = applyDefaultDate(rawMaxDate, null, inputFormat);
  const minDateProp = applyDefaultDate(rawMinDate, null, inputFormat);
  const originalValue = value ? format(value, inputFormat) : null;
  const maxDate = applyDefaultDate(maxDateProp, defaultMaxDate, inputFormat);
  const minDate = applyDefaultDate(minDateProp, defaultMinDate, inputFormat);
  const helperText =
    helperType === 'success' && !helperProp ? 'Fecha ingresada correctamente' : helperProp;

  const inputMask = inputFormat.replace(/[a-zA-Z]/g, '0');

  const validViews: ('year' | 'month' | 'date')[] = useMemo(() => ['year', 'month', 'date'], []);
  const filteredViews = useMemo(
    () => (views ? views.filter((view) => validViews.includes(view)) : []),
    [views, validViews],
  );
  const today = useMemo(() => new Date(), []);

  const getDefaultView = () => {
    if (filteredViews.includes('year')) return 'year';
    return 'month';
  };

  const baseElementRef = useRef(null);
  const [dynamicWidth, setDynamicWidth] = useState<string>(fullWidth ? '100%' : '325px');
  const [debouncedValue, setDebouncedValue] = useState<string | null>(originalValue);
  const [isCalendarOpen, setCalendarOpen] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [selectedView, setSelectedView] = useState<'year' | 'month' | 'date'>(
    openTo || getDefaultView(),
  );
  const [selectedValues, setSelectedValues] = useState<SelectedValues>(
    getDefaultDateBuilder(value),
  );

  const [possibleDates, setPossibleDates] = useState<DateSlotBase[]>(
    getCalendarContent(new Date(selectedValues.year, selectedValues.month)),
  );

  const handleSelectedValuesChange = ({ year, month, date, range }: SelectedValues = {}) => {
    setSelectedValues((oldValues) => ({
      ...oldValues,
      ...(year !== undefined ? { year } : {}),
      ...(month !== undefined ? { month } : {}),
      ...(date !== undefined ? { date } : {}),
      ...(range !== undefined ? { range } : {}),
    }));
  };

  const handleDisableYear = (year: number) => {
    if (shouldDisableYear(year)) return true;
    if (disablePast && year < today.getFullYear()) return true;
    if (disableFuture && year > today.getFullYear()) return true;
    if (minDate && year < minDate.getFullYear()) return true;
    if (maxDate && year > maxDate.getFullYear()) return true;
    return false;
  };
  const handleDisableMonth = (month: number, year: number) => {
    if (shouldDisableMonth(month, year)) return true;
    if (disablePast && year === today.getFullYear() && month < today.getMonth()) return true;
    if (disableFuture && year === today.getFullYear() && month > today.getMonth()) return true;
    if (minDate && year === minDate.getFullYear() && month < minDate.getMonth()) return true;
    if (maxDate && year === maxDate.getFullYear() && month > maxDate.getMonth()) return true;
    if (handleDisableYear(year)) return true;
    return false;
  };
  const handleDisableDate = (date: Date) => {
    if (shouldDisableDate(date)) return true;
    if (disablePast && compareTwoDates(date, today, 'less')) return true;
    if (disableFuture && compareTwoDates(date, today, 'greater')) return true;
    if (minDate && compareTwoDates(date, minDate, 'less')) return true;
    if (maxDate && compareTwoDates(date, maxDate, 'greater')) return true;
    if (handleDisableMonth(date.getMonth(), date.getFullYear())) return true;
    return false;
  };

  const handleDateChange = (date: Date) => setDebouncedValue(format(date, inputFormat));

  const handleCancel = () => {
    setSelectedValues(getDefaultDateBuilder(value));
    setDebouncedValue(originalValue);
    setCalendarOpen(false);
  };

  const handleConfirmation = () => {
    onChange(isWeekly ? selectedValues.range : selectedValues.date);
    setCalendarOpen(false);
  };

  useEffect(() => {
    if (baseElementRef.current) {
      setDynamicWidth(fullWidth ? '100%' : `${baseElementRef.current.offsetWidth}px`);
    }
  }, [baseElementRef.current, fullWidth]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (isValidDateString(debouncedValue, removeSpecialCharacters(inputFormat))) {
        const newDate = parse(debouncedValue, removeSpecialCharacters(inputFormat), new Date());
        handleSelectedValuesChange({
          year: newDate.getFullYear(),
          month: newDate.getMonth(),
          date: newDate,
        });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [debouncedValue]);

  useEffect(() => setSelectedView(openTo || getDefaultView()), [views, openTo]);

  useEffect(() => {
    setPossibleDates(() => {
      if (selectedValues.year !== null && selectedValues.month !== null)
        return getCalendarContent(new Date(selectedValues.year, selectedValues.month));
      return getCalendarContent();
    });
  }, [selectedValues.year, selectedValues.month]);

  useEffect(() => {
    const { date: selectedDate } = selectedValues || {};
    if (!selectedDate) {
      if (isWeekly) handleSelectedValuesChange({ range: null });
      setDebouncedValue(null);
    }
    if (isValidDate(selectedDate)) {
      if (isWeekly) handleSelectedValuesChange({ range: getWeekSelectedWeekLimits(selectedDate) });
      handleDateChange(selectedDate);
    }
  }, [selectedValues.date]);

  useEffect(() => {
    if (['year', 'month'].includes(openTo)) setIsOpen(true);
  }, [openTo]);

  const renderCalendar = () => (
    <Calendar
      anchorOrigin={anchorOrigin}
      desktopMode={desktopMode}
      disabled={disabled}
      filteredViews={filteredViews}
      handleCancel={handleCancel}
      handleConfirmation={handleConfirmation}
      handleDisableDate={handleDisableDate}
      handleDisableMonth={handleDisableMonth}
      handleDisableYear={handleDisableYear}
      inputFormat={inputFormat}
      isOpen={isOpen}
      isWeekly={isWeekly}
      maxDate={maxDate}
      minDate={minDate}
      possibleDates={possibleDates}
      selectedValues={selectedValues}
      selectedView={selectedView}
      handleSelectedValuesChange={handleSelectedValuesChange}
      setSelectedView={setSelectedView}
      setIsOpen={setIsOpen}
      setDebouncedValue={setDebouncedValue}
      value={value}
      views={views}
    />
  );

  return (
    <DatePickerWrapperStyled dynamicWidth={dynamicWidth} style={style}>
      {baseElement ? (
        <Box
          ref={baseElementRef}
          onClick={() => setCalendarOpen((previousStatus) => !previousStatus)}
        >
          {baseElement}
        </Box>
      ) : (
        <TextField
          disabled={disabled}
          helperText={helperText}
          helperType={helperType}
          fullWidth={fullWidth}
          label={label}
          mask={isWeekly ? `${inputMask} - ${inputMask}` : inputMask}
          placeholder={isWeekly ? `${inputFormat} - ${inputFormat}` : inputFormat}
          onChange={(textValue) => setDebouncedValue(textValue)}
          onFieldClick={() => setCalendarOpen((previousStatus) => !previousStatus)}
          onlyLabel
          startAdornment={
            <Icon
              name="calendarTodayFilled"
              color={disabled ? TucarTheme.colors.gray700 : TucarTheme.colors.blue500}
            />
          }
          value={
            isWeekly
              ? `${
                  selectedValues.range ? format(selectedValues.range?.startDate, inputFormat) : ''
                } - ${
                  selectedValues.range ? format(selectedValues.range?.endDate, inputFormat) : ''
                }`.trim()
              : debouncedValue
          }
          {...((!desktopMode || isWeekly) && { readOnly: true })}
          {...(desktopMode &&
            !isWeekly && {
              onKeyDown: (event, key) => {
                if (key === 'Enter') {
                  const dateToSubmit = isValidDateString(
                    debouncedValue,
                    removeSpecialCharacters(inputFormat),
                  )
                    ? parse(debouncedValue, removeSpecialCharacters(inputFormat), new Date())
                    : null;
                  if (dateToSubmit && !handleDisableDate(dateToSubmit)) {
                    onChange(dateToSubmit);
                    event.preventDefault();
                  }
                }
              },
            })}
          {...(loading && { endAdornment: <Loader data-testid="loading" size={16} /> })}
        />
      )}
      {isCalendarOpen && desktopMode && renderCalendar()}
      {!desktopMode && (
        <Modal
          {...(isCalendarOpen ? { 'data-testid': 'calendar-modal' } : {})}
          onClose={() => setCalendarOpen(false)}
          open={isCalendarOpen}
          style={{ overflow: 'scroll' }}
        >
          {renderCalendar()}
        </Modal>
      )}
    </DatePickerWrapperStyled>
  );
};

DatePicker.defaultProps = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'center' },
  disablePast: false,
  disableFuture: false,
  inputFormat: 'dd/MM/yyyy',
  loading: false,
  shouldDisableDate: () => false,
  shouldDisableMonth: () => false,
  shouldDisableYear: () => false,
  views: ['year', 'month', 'date'],
};

export default DatePicker;
